import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type Device } from '@/models/device/Device';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';

interface Props {
    open: boolean;
    device: Device | null;
    onClose: () => void;
}

// One label/value row. Always renders the label — a blank/null value shows an em dash rather than hiding.
const Field = ({ label, value }: { label: string; value?: string | number | null }) => {
    const display = value === null || value === undefined || value === '' ? '—' : value;
    return (
        <Box sx={{ display: 'flex', gap: 1.5, py: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold', minWidth: 140, flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{display}</Typography>
        </Box>
    );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="subtitle2"
        sx={{
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            px: 1,
            py: 0.5,
            mb: 1,
            borderRadius: 0.5,
            bgcolor: (theme) => (theme.palette.mode === 'light' ? '#eef2f6' : theme.palette.action.hover),
            color: (theme) => (theme.palette.mode === 'light' ? '#334e68' : theme.palette.text.primary),
        }}
    >
        {children}
    </Typography>
);

// A section header plus its fields laid out in two columns.
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Box sx={{ mt: 2 }}>
        <SectionLabel>{title}</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3 }}>{children}</Box>
    </Box>
);

const DeviceDetailDialog = ({ open, device, onClose }: Props) => {
    const strings = useLocalizedStrings();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{strings.deviceDetails}</DialogTitle>
            <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
                {device && (
                    <>
                        <Section title={strings.details}>
                            <Field label={strings.deviceName} value={device.externalName} />
                            <Field label={strings.enabled} value={device.enabled ? strings.yes : strings.no} />
                            <Field label={strings.manufacturer} value={device.manufacturer} />
                            <Field label={strings.brand} value={device.brand} />
                            <Field label={strings.model} value={device.model} />
                        </Section>

                        <Section title={strings.operatingSystem}>
                            <Field label={strings.osVersion} value={device.osVersion} />
                            <Field label={strings.osFriendlyVersion} value={device.osFriendlyVersion} />
                            <Field label={strings.osSecurityPatchLevel} value={device.osSecurityPatchLevel} />
                            <Field label={strings.osOemBuildNumber} value={device.osOemBuildNumber} />
                        </Section>

                        <Section title={strings.identifiers}>
                            <Field label={strings.deviceId} value={device.deviceId} />
                            <Field label={strings.externalId} value={device.externalId} />
                        </Section>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{strings.close}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeviceDetailDialog;
